/**
 * Browser-only download utilities.
 *
 * ⚠️ WARNING: This module requires browser runtime (DOM APIs).
 * Do NOT import in Node.js or server-side code.
 *
 * @module @f2e/shared/browser/download
 */
/**
 * Triggers a file download from a Blob object.
 *
 * ⚠️ Browser-only: Requires DOM APIs (document, window.URL)
 *
 * @param blob - The blob data to download
 * @param filename - The filename for the downloaded file
 *
 * @example
 * ```ts
 * const blob = new Blob(['Hello, World!'], { type: 'text/plain' })
 * triggerBlobDownload(blob, 'hello.txt')
 * ```
 */
export const triggerBlobDownload = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
};
/**
 * Fetches a file from a URL and triggers a download.
 *
 * ⚠️ Browser-only: Requires browser Fetch API and DOM APIs
 *
 * @param url - The URL to fetch the file from
 * @param filename - The filename for the downloaded file
 * @param options - Optional fetch configuration (headers, credentials)
 * @returns Promise<boolean> - true if download succeeded, false otherwise
 *
 * @example
 * ```ts
 * // Simple download
 * await downloadFile('https://example.com/file.pdf', 'document.pdf')
 *
 * // Download with authentication
 * await downloadFile('https://api.example.com/file.pdf', 'document.pdf', {
 *   headers: { 'x-api-key': 'your-api-key' },
 *   credentials: 'include',
 * })
 * ```
 */
export const downloadFile = async (url, filename, options) => {
    try {
        const response = await fetch(url, {
            method: "GET",
            credentials: options?.credentials ?? "include",
            headers: options?.headers,
        });
        if (!response.ok) {
            console.error("[download] Download failed:", {
                status: response.status,
                statusText: response.statusText,
                url,
                filename,
            });
            return false;
        }
        const blob = await response.blob();
        triggerBlobDownload(blob, filename);
        return true;
    }
    catch (error) {
        console.error("[download] Fetch error:", { url, filename, error });
        return false;
    }
};
